import Script from 'next/script'

interface BreadcrumbItem {
  name: string
  url: string
}

interface BreadcrumbSchemaProps {
  items: BreadcrumbItem[]
}

export default function BreadcrumbSchema({ items }: BreadcrumbSchemaProps) {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": items.map((item, index) => ({
      "@type": "ListItem",
      "position": index + 1,
      "name": item.name,
      "item": item.url
    }))
  }

  return (
    <Script
      id="breadcrumb-schema"
      type="application/ld+json"
      dangerouslySetInnerHTML={{
        __html: JSON.stringify(breadcrumbSchema)
      }}
    />
  )
}

// Helper function to generate breadcrumbs for listing pages
export function generateListingBreadcrumbs(
  country: string,
  type?: string,
  state?: string,
  city?: string,
  listingType: 'rent' | 'sale' | 'reviews' = 'rent'
): BreadcrumbItem[] {
  const breadcrumbs: BreadcrumbItem[] = [
    { name: "Home", url: "https://rentwise.co" },
    { name: "Listings", url: "https://rentwise.co/listings" }
  ]

  // Add listing type
  breadcrumbs.push({
    name: listingType === 'reviews' ? 'Reviews' : (listingType === 'rent' ? 'For Rent' : 'For Sale'),
    url: `https://rentwise.co/listings/${listingType}`
  })

  // Add country
  if (country) {
    breadcrumbs.push({
      name: country.charAt(0).toUpperCase() + country.slice(1),
      url: `https://rentwise.co/listings/${listingType}/${country}`
    })
  }

  // Add property type
  if (type) {
    const formattedType = type.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    breadcrumbs.push({
      name: formattedType,
      url: `https://rentwise.co/listings/${listingType}/${country}/${type}`
    })
  }

  // Add state
  if (state) {
    const formattedState = state.charAt(0).toUpperCase() + state.slice(1)
    breadcrumbs.push({
      name: formattedState,
      url: `https://rentwise.co/listings/${listingType}/${country}/${type}/${state}`
    })
  }

  // Add city
  if (city) {
    const formattedCity = city.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())
    breadcrumbs.push({
      name: formattedCity,
      url: `https://rentwise.co/listings/${listingType}/${country}/${type}/${state}/${city}`
    })
  }

  return breadcrumbs
} 